import React, { useState, useEffect, useMemo } from "react";
import { func, string } from "prop-types";
import { Formik, Form, Field } from "formik";
import { prop, equals } from "ramda";
import { Row, Col, Button } from "reactstrap";
import { bool } from "yup";

import LegalEntityFormFields from "./legal-entity-form-fields";
import NaturalPersonFormFields from "./natural-person-form-fields";
import {
  FieldSpy,
  ButtonSelectType,
  Input,
} from "../../../../components/formik";
import { optionsType, watchListEntryType } from "../../../../prop-types";
import { selectInitialState } from "./initial-values";
import { ENTRY_TYPE } from "../../constants/watchListEntryConstants";

import "./upsertWatchListEntryForm.css";
import PageTitle from "../../../../components/common/PageTitle";
import ButtonSelectCategory from "../../../../components/formik/upsertWatchlistEntryForm/ButtonSelectCategory";
import { withError } from "../../../../components/hocs";
import {
  watchListLegalEntityEntryValidationSchema,
  watchListNaturalPersonEntryValidationSchema,
} from "../../validation-schemas/watchListEntryValidationSchema";
import FullscreenLoader from "../../../../layouts/FullScreenLoader";
import useRoles from "../../../../components/hooks/useRoles";

const ErrorInput = withError(Input);

const UpsertWatchListEntryForm = ({
  title,
  handleSubmit,
  countries,
  nationalities,
  initialValues,
  isLoading,
}) => {
  const { isCustomerSupport } = useRoles();
  const AdditionalFields = ({ isLegalEntity, countries, ...props }) =>
    isLegalEntity ? (
      <LegalEntityFormFields {...props} countries={countries} />
    ) : (
      <NaturalPersonFormFields {...props} countries={countries} />
    );

  const [isLegalEntity, setIsLegalEntity] = useState(
    equals(ENTRY_TYPE.LEGAL_ENTITY, prop("type", initialValues))
  );

  const [selectedInitialValues, setSelectedInitialValues] = useState(
    initialValues || selectInitialState(initialValues, isLegalEntity)
  );

  const fields = useMemo(() => {
    return (
      <AdditionalFields
        isLegalEntity={isLegalEntity}
        nationalities={nationalities}
        countries={countries}
      />
    );
  }, [selectedInitialValues, nationalities, countries, isLegalEntity]);

  useEffect(() => {
    setSelectedInitialValues(
      selectInitialState(selectedInitialValues, isLegalEntity)
    );
  }, [isLegalEntity]);

  const handleIsLegalEntityChange = (type) => {
    setIsLegalEntity(type === ENTRY_TYPE.LEGAL_ENTITY);
  };

  const modifiedSubmit = (data) => {
    // for the reason of hiding [isPolitician] field
    if (!isLegalEntity) {
      handleSubmit({ ...data, isPolitician: false });
    } else {
      handleSubmit(data);
    }
  };

  const validationSchema = isLegalEntity
    ? watchListLegalEntityEntryValidationSchema
    : watchListNaturalPersonEntryValidationSchema;

  return (
    <div className="upsert-watch-list-entry-main-container">
      <PageTitle title={title} className="bg-white" />
      <FullscreenLoader loading={isLoading} />
      <Col lg={8} className="my-auto mx-auto ">
        <Formik
          enableReinitialize
          onSubmit={modifiedSubmit}
          initialValues={selectedInitialValues}
          validationSchema={validationSchema}
          validateOnBlur={false}
          validateOnChange={false}
        >
          <Form className="upsert-watch-list-entry-form-container">
            <Row className="mt-4">
              <Col lg={5} className="p-0 ">
                <Field
                  name="type"
                  label="Type"
                  component={ButtonSelectType}
                  options={[
                    { value: 1, label: "Natural person" },
                    { value: 2, label: "Legal entity" },
                  ]}
                />
                <FieldSpy name="type" onChange={handleIsLegalEntityChange} />
              </Col>
              <Col lg={7} className="pl-3">
                <Field
                  name="category"
                  label="Category"
                  className="dropdown-group"
                  options={[
                    { value: "CustomPep", label: "PEP" },
                    { value: "CustomMwp", label: "MWP" },
                    { value: "CustomBlk", label: "BLK" },
                    { value: "CustomSan", label: "SAN" },
                    { value: "CustomTerr", label: "TERR" },
                    { value: "CustomPepRelated", label: "PEPR" },
                    { value: "CustomYwl", label: "YWL" },
                  ]}
                  component={ButtonSelectCategory}
                />

                <FieldSpy name="type" onChange={handleIsLegalEntityChange} />
              </Col>
            </Row>

            <Row className="mt-4">
              <Col md={12}>{fields}</Col>
              <Col className="mt-4">
                <Field
                  component={ErrorInput}
                  type="textarea"
                  placeholder="Comment"
                  name="comment"
                />
              </Col>
            </Row>
            <Col className="text-center ">
              <Button
                className="upsert-watchlist-entry mt-3 pl-5 pr-5 mb-3"
                type="submit"
                disabled={isCustomerSupport}
              >
                Save
              </Button>
            </Col>
          </Form>
        </Formik>
      </Col>
    </div>
  );
};

UpsertWatchListEntryForm.propTypes = {
  title: string.isRequired,
  handleSubmit: func.isRequired,
  countries: optionsType.isRequired,
  nationalities: optionsType.isRequired,
  initialValues: watchListEntryType,
  disabledEntryTypeSelection: bool,
};

export default UpsertWatchListEntryForm;



// WEBPACK FOOTER //
// ./src/features/watch-list-entry/components/upsert-watch-list-entry-form/UpsertWatchListEntryForm.jsx